import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import {
  useGfsAssessmentGetQuery,
  useGfsAssessmentPutMutation,
} from "@/services/backend/gfs/assessment/api";
import {
  GfsAreaAssessment,
  GfsAssessment,
} from "@/services/backend/gfs/assessment/types";
import { useGfsCatalogGetQuery } from "@/services/backend/gfs/catalog/api";
import { GfsCatalogItem } from "@/services/backend/gfs/catalog/types";
import { usePestcontrolPestListQuery } from "@/services/backend/pestcontrol/api/pest-api";
import { Pest, PestKind } from "@/services/backend/pestcontrol/types/pest";
import {
  GFS_AREA_TYPE_INSIDE,
  GfsAreaType,
} from "@/services/backend/gfs/assessment/components/gfs-site-selector-components";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigationOutlet,
} from "@/shared/bsm/core/document/components/ui/document-page";
import {
  AssessmentEditorContext,
  type AssessmentUpdateOptions,
} from "@/services/backend/gfs/assessment/components/assessment-editor-context";
import { AssessmentSectionNavigation } from "@/services/backend/gfs/assessment/components/assessment-section-navigation";
import { DeleteAssessmentDialog } from "@/services/backend/gfs/assessment/components/delete-assessment-dialog";
import { SetGfsAssessmentImmutableButton } from "@/services/backend/gfs/assessment/components/set-assessment-immutable-button";

export function GfsAssessmentEditor() {
  const { processId = "", assessmentId = "" } = useParams();
  const { data: process, error: processError } = useProcessesProcessShowQuery({
    id: processId,
  });
  const { data: assessment, error: assessmentError } = useGfsAssessmentGetQuery(
    { id: assessmentId },
    { skip: !assessmentId },
  );

  if (processError) return <RTKQueryErrorAlert error={processError} />;
  if (assessmentError) return <RTKQueryErrorAlert error={assessmentError} />;
  if (!process?.customerId || !assessment) return null;

  return (
    <PersistenceContextProvider>
      <AssessmentEditor key={assessment.id} assessment={assessment} />
    </PersistenceContextProvider>
  );
}

function AssessmentEditor({ assessment }: { assessment: GfsAssessment }) {
  const [initialPutRequest] = useState(() => ({
    id: assessment.id,
    assessment: normalizeGfsAssessment(assessment),
  }));
  const [putRequest, setPutRequest] = useState(() => ({
    id: assessment.id,
    assessment: normalizeGfsAssessment(assessment),
  }));
  const [isImmutable, setIsImmutable] = useState(assessment.immutable);
  const draft = putRequest.assessment;
  const [manualPestIDs, setManualPestIDs] = useState<string[]>();
  const [areaPersistenceStates, setAreaPersistenceStates] = useState<
    Record<string, PersistenceState>
  >({});
  const [workTimeInputResetKey, setWorkTimeInputResetKey] = useState(0);
  const [areaScope, setAreaScope] = useState({
    buildingId: "",
    levelId: "",
    areaType: GFS_AREA_TYPE_INSIDE as GfsAreaType,
  });
  const { data: pests } = usePestcontrolPestListQuery({
    company: { active: true, values: [assessment.companyId] },
    kind: PestKind.Category,
    limit: 500,
  });
  const { data: catalog } = useGfsCatalogGetQuery(
    { id: assessment.catalogId },
    { skip: !assessment.catalogId },
  );
  const catalogItems = useMemo(() => catalog?.items ?? [], [catalog?.items]);
  const derivedPestSnapshots = useMemo(
    () =>
      derivePestSnapshotsFromMonitors(
        draft.areaAssessments ?? [],
        catalogItems,
      ),
    [catalogItems, draft.areaAssessments],
  );
  const accumulatedLaborDuration = useMemo(
    () => sumAreaDuration(draft.areaAssessments ?? [], "laborDuration"),
    [draft.areaAssessments],
  );
  const globalLaborDuration = Math.max(
    draft.workEstimate.duration ?? 0,
    accumulatedLaborDuration,
  );
  const derivedPestIDs = useMemo(
    () =>
      new Set(
        derivedPestSnapshots.map((pestSnapshot) => pestSnapshot.sourceId),
      ),
    [derivedPestSnapshots],
  );
  const selectedPestIDs = useMemo(
    () =>
      Array.from(
        new Set([...(manualPestIDs ?? []), ...Array.from(derivedPestIDs)]),
      ),
    [derivedPestIDs, manualPestIDs],
  );
  const saveMutation = useGfsAssessmentPutMutation();
  const [, saveState] = saveMutation;
  const dirtyAreaIds = useRef(new Set<string>());

  const setAreasPersistenceState = useCallback(
    (areaIds: string[], state: PersistenceState) => {
      if (areaIds.length === 0) {
        return;
      }
      setAreaPersistenceStates((current) => {
        const next = { ...current };
        areaIds.forEach((areaId) => {
          next[areaId] = state;
        });
        return next;
      });
    },
    [],
  );

  useDebouncedMutation(
    putRequest,
    setPutRequest,
    saveMutation,
    initialPutRequest,
    {
      delay: 500,
      resetOnError: false,
      toastError: true,
      onSuccess: () => {
        const submittedAreaIds = Array.from(dirtyAreaIds.current);
        setAreasPersistenceState(submittedAreaIds, PersistenceState.SAVED);
        dirtyAreaIds.current.clear();
      },
      onError: () => {
        setAreasPersistenceState(
          Array.from(dirtyAreaIds.current),
          PersistenceState.ERROR,
        );
      },
    },
  );

  const updateAssessment = useCallback(
    (
      update:
        | Partial<GfsAssessment>
        | ((current: GfsAssessment) => Partial<GfsAssessment>),
      options: AssessmentUpdateOptions = {},
    ) => {
      if (isImmutable) {
        return;
      }

      if (options.markPersistence !== false && options.areaId) {
        dirtyAreaIds.current.add(options.areaId);
        setAreasPersistenceState([options.areaId], PersistenceState.SAVING);
      }

      setPutRequest((currentRequest) => {
        const patch =
          typeof update === "function"
            ? update(currentRequest.assessment)
            : update;
        const areaAssessments =
          patch.areaAssessments ??
          currentRequest.assessment.areaAssessments ??
          [];
        const nextAssessment = {
          ...currentRequest.assessment,
          ...patch,
          areaAssessments,
          updatedAt: new Date().toISOString(),
        };
        const normalizedAssessment =
          patch.areaAssessments === undefined
            ? nextAssessment
            : enforceTimeMinimums(nextAssessment);

        return {
          ...currentRequest,
          assessment: normalizedAssessment,
        };
      });
    },
    [isImmutable, setAreasPersistenceState],
  );

  const handleAssessmentImmutable = useCallback(() => {
    setIsImmutable(true);
  }, []);

  useEffect(() => {
    if ((draft.workEstimate.duration ?? 0) >= accumulatedLaborDuration) {
      return;
    }

    updateAssessment((current) => ({
      workEstimate: {
        ...current.workEstimate,
        duration: Math.max(
          current.workEstimate.duration ?? 0,
          accumulatedLaborDuration,
        ),
      },
    }));
  }, [accumulatedLaborDuration, draft.workEstimate.duration, updateAssessment]);

  useEffect(() => {
    if (!catalog) {
      return;
    }
    if (manualPestIDs !== undefined) {
      return;
    }

    setManualPestIDs(
      targetPestIDsFromAssessment(assessment).filter(
        (pestID) => !derivedPestIDs.has(pestID),
      ),
    );
  }, [assessment, catalog, derivedPestIDs, manualPestIDs]);

  useEffect(() => {
    if (!catalog || manualPestIDs === undefined) {
      return;
    }

    const now = new Date().toISOString();
    const pestCategories = selectedPestSnapshots(
      pests?.items ?? [],
      selectedPestIDs,
      draft.pestCategories,
      derivedPestSnapshots,
      now,
    );

    if (sameAssessmentPestCategories(draft, pestCategories)) {
      return;
    }

    updateAssessment({
      pestCategories,
      areaAssessments: (draft.areaAssessments ?? []).map((areaAssessment) => ({
        ...areaAssessment,
        pestCategories,
      })),
    });
  }, [
    catalog,
    derivedPestSnapshots,
    draft,
    manualPestIDs,
    pests?.items,
    selectedPestIDs,
    updateAssessment,
  ]);

  const persistCurrentBuildings = async (
    nextBuildings: GfsAssessment["buildings"],
  ) => {
    updateAssessment({ buildings: nextBuildings });
    return true;
  };

  return (
    <AssessmentEditorContext.Provider
      value={{
        assessment,
        draft,
        areaScope,
        areaPersistenceStates,
        catalogItems: catalog?.items ?? [],
        catalogConsumables: catalog?.consumables ?? [],
        pests: pests?.items ?? [],
        selectedPestIDs,
        derivedPestIDs,
        accumulatedLaborDuration,
        globalLaborDuration,
        workTimeInputResetKey,
        setWorkTimeInputResetKey,
        setManualPestIDs,
        setAreaScope,
        updateAssessment,
        persistCurrentBuildings,
      }}
    >
      <DocumentPage>
        <DocumentPageHeader>
          <AssessmentSectionNavigation />
          <DocumentPageActions>
            <PersistenceStateUsingContext className="mr-2" />
            <SetGfsAssessmentImmutableButton
              assessmentId={assessment.id}
              immutable={isImmutable}
              onImmutable={handleAssessmentImmutable}
            />
            <DeleteAssessmentDialog
              assessmentId={assessment.id}
              disabled={isImmutable}
            />
          </DocumentPageActions>
        </DocumentPageHeader>
        <DocumentPageContent>
          {saveState.error && <RTKQueryErrorAlert error={saveState.error} />}
          <fieldset disabled={isImmutable} className="min-w-0 border-0 p-0">
            <DocumentPageNavigationOutlet />
          </fieldset>
        </DocumentPageContent>
      </DocumentPage>
    </AssessmentEditorContext.Provider>
  );
}

function normalizeGfsAssessment(assessment: GfsAssessment): GfsAssessment {
  return enforceTimeMinimums({
    ...assessment,
    workEstimate: {
      technicians: assessment.workEstimate.technicians ?? 0,
      duration: assessment.workEstimate.duration ?? 0,
      travelDuration: assessment.workEstimate.travelDuration ?? 0,
      trips: assessment.workEstimate.trips ?? 0,
      measureCount: assessment.workEstimate.measureCount ?? 0,
      measurePeriodValue: assessment.workEstimate.measurePeriodValue ?? 0,
      measurePeriodUnit: assessment.workEstimate.measurePeriodUnit ?? "",
      eradicationMonitoring:
        assessment.workEstimate.eradicationMonitoring ?? false,
      eradicationMonitoringDuration:
        assessment.workEstimate.eradicationMonitoringDuration ?? 0,
    },
    areaAssessments: (assessment.areaAssessments ?? []).map(
      (areaAssessment) => ({
        ...areaAssessment,
        laborDuration: areaAssessment.laborDuration ?? 0,
        areaSizeM2: areaAssessment.areaSizeM2 ?? 0,
        materials: areaAssessment.materials ?? [],
      }),
    ),
  });
}

function enforceTimeMinimums(assessment: GfsAssessment): GfsAssessment {
  const accumulatedLaborDuration = sumAreaDuration(
    assessment.areaAssessments ?? [],
    "laborDuration",
  );

  return {
    ...assessment,
    workEstimate: {
      ...assessment.workEstimate,
      duration: Math.max(
        assessment.workEstimate.duration ?? 0,
        accumulatedLaborDuration,
      ),
    },
  };
}

function sumAreaDuration(
  areaAssessments: GfsAreaAssessment[],
  field: "laborDuration",
): number {
  return areaAssessments.reduce(
    (total, areaAssessment) => total + (areaAssessment[field] ?? 0),
    0,
  );
}

function targetPestIDsFromAssessment(assessment: GfsAssessment): string[] {
  return Array.from(
    new Set(
      [
        ...assessment.pestCategories,
        ...(assessment.areaAssessments ?? []).flatMap(
          (areaAssessment) => areaAssessment.pestCategories,
        ),
      ].map((pest) => pest.sourceId),
    ),
  );
}

function selectedPestSnapshots(
  pests: Pest[],
  selectedIDs: string[],
  fallback: GfsAssessment["pestCategories"],
  derived: GfsAssessment["pestCategories"],
  takenAt: string,
): GfsAssessment["pestCategories"] {
  const derivedByID = new Map(
    derived.map((pestSnapshot) => [pestSnapshot.sourceId, pestSnapshot]),
  );
  return selectedIDs.flatMap((id) => {
    const derivedSnapshot = derivedByID.get(id);
    if (derivedSnapshot) {
      return [normalizePestSnapshot(derivedSnapshot)];
    }
    const pest = pests.find((candidate) => candidate.id === id);
    if (pest) return [toSnapshot(pest, takenAt)];
    const snapshot = fallback.find((candidate) => candidate.sourceId === id);
    return snapshot ? [normalizePestSnapshot(snapshot)] : [];
  });
}

function derivePestSnapshotsFromMonitors(
  areaAssessments: GfsAreaAssessment[],
  catalogItems: GfsCatalogItem[],
): GfsAssessment["pestCategories"] {
  const catalogItemsByID = new Map(
    catalogItems.map((item) => [item.id, item] as const),
  );
  const derivedByID = new Map<
    string,
    GfsAssessment["pestCategories"][number]
  >();

  areaAssessments.forEach((areaAssessment) => {
    areaAssessment.monitors.forEach((monitor) => {
      const catalogItem = catalogItemsByID.get(monitor.catalogItemId);
      catalogItem?.pests
        .filter((pestSnapshot) => pestSnapshot.kind === PestKind.Category)
        .forEach((pestSnapshot) => {
          derivedByID.set(
            pestSnapshot.sourceId,
            normalizePestSnapshot(pestSnapshot),
          );
        });
    });
  });

  return Array.from(derivedByID.values());
}

function samePestIDs(
  left: GfsAssessment["pestCategories"],
  right: GfsAssessment["pestCategories"],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const leftIDs = new Set(left.map((pestSnapshot) => pestSnapshot.sourceId));
  return right.every((pestSnapshot) => leftIDs.has(pestSnapshot.sourceId));
}

function sameAssessmentPestCategories(
  assessment: GfsAssessment,
  pestCategories: GfsAssessment["pestCategories"],
): boolean {
  return (
    samePestIDs(assessment.pestCategories, pestCategories) &&
    (assessment.areaAssessments ?? []).every((areaAssessment) =>
      samePestIDs(areaAssessment.pestCategories, pestCategories),
    )
  );
}

function toSnapshot(pest: Pest, takenAt: string) {
  return {
    companyId: pest.companyId,
    sourceId: pest.id,
    kind: pest.kind,
    group: pest.group,
    name: pest.name,
    technicalTerm: pest.technicalTerm,
    abbreviation: pest.abbreviation,
    description: pest.description,
    synonyms: pest.synonyms,
    takenAt,
  };
}

function normalizePestSnapshot(
  pestSnapshot: GfsAssessment["pestCategories"][number],
): GfsAssessment["pestCategories"][number] {
  return {
    companyId: pestSnapshot.companyId,
    sourceId: pestSnapshot.sourceId,
    kind: pestSnapshot.kind,
    group: pestSnapshot.group,
    name: pestSnapshot.name,
    technicalTerm: pestSnapshot.technicalTerm,
    abbreviation: pestSnapshot.abbreviation,
    description: pestSnapshot.description,
    synonyms: pestSnapshot.synonyms ?? [],
    takenAt: pestSnapshot.takenAt,
  };
}
